import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, PaletteUtils } from '@shared/utils';
import { ColorsEnum, Icon, Select, TablePaginationCountOfItems, Text } from '@shared/widgets';
import FiChevronIcon from '@assets/icons/fi-chevron.svg';
import ChevronIcon from '@assets/icons/chevron.svg';

interface IPaginationProps {
  className?: string;
  currentPage: number;
  totalItemCount: number;
  countItemPage: TablePaginationCountOfItems;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
  noSelect?: boolean;
  perPageLabel?: string;
}

export const Pagination = (props: IPaginationProps) => {
  const { t } = useTranslation();

  const totalPages = useMemo(() => {
    if (props.totalItemCount < props.countItemPage) return 1;
    const result = Math.ceil(props.totalItemCount / props.countItemPage);
    if (isNaN(result)) return 1;
    return result;
  }, [props.totalItemCount, props.countItemPage]);

  useEffect(() => {
    if (totalPages < props.currentPage) props.onChangePage(1);
  }, [totalPages]);

  const onChangePage = (page: number) => props.onChangePage(page);

  const firstItemNumber = useMemo(() => {
    if (props.totalItemCount === 0) return 0;
    return props.currentPage == 1 ? 1 : (props.currentPage - 1) * props.countItemPage + 1;
  }, [props.currentPage, props.countItemPage, props.totalItemCount]);
  const lastItemNumber = useMemo(() => {
    const result = props.currentPage * props.countItemPage;
    if (props.totalItemCount < result) return props.totalItemCount;
    return result;
  }, [props.currentPage, props.totalItemCount, props.countItemPage]);
  return (
    <div className={cn('flex min-h-9 justify-between', PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-4', props.className)}>
      <div className={cn('flex', PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-3')}>
        <Text className='my-auto text-nowrap' size='sm' color={ColorsEnum.Secondary}>
          {t('pagination.page', { currentPage: props.currentPage, totalPages })}
        </Text>
        <div
          className={cn(
            'mx-4 flex',
            PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-soft-black',
            PaletteUtils.isVcfcPalette && ['rounded-xl bg-white dark:bg-eerie-black', 'max-md:mx-0 max-md:h-10 max-md:w-full max-md:justify-between'],
          )}
        >
          <div
            className={cn(
              'flex cursor-pointer items-center justify-center px-3 transition-colors duration-200',
              props.currentPage <= 1 && 'bg-light dark:bg-slate dark:opacity-60',
              PaletteUtils.isInsigniaPalette && [
                props.currentPage > 1 && ['bg-soft-black dark:bg-slate', 'hover:bg-soft-black/80 dark:hover:bg-slate/60'],
              ],
              PaletteUtils.isVcfcPalette && [
                'rounded-bl-xl rounded-tl-xl',
                props.currentPage > 1 && ['bg-primary dark:bg-white/50', 'hover:bg-primary/80 dark:hover:bg-white/70'],
              ],
            )}
            onClick={() => props.currentPage > 1 && onChangePage(1)}
          >
            <Icon icon={<FiChevronIcon />} color={PaletteUtils.getColor('primary', 'white')} />
          </div>
          <div
            className={cn(
              'flex cursor-pointer items-center px-3 opacity-30 transition-colors duration-200',
              props.currentPage > 1 && 'opacity-100 hover:bg-gainsborough dark:hover:bg-almost-light-gray',
            )}
            onClick={() => props.currentPage > 1 && onChangePage(props.currentPage - 1)}
          >
            <Icon className={cn('dark:text-white/80', props.currentPage > 1 && 'text-almost-light-gray')} icon={<ChevronIcon />} />
          </div>
          <p className='my-auto cursor-pointer text-nowrap px-3 text-sm text-slate dark:text-white'>{props.currentPage}</p>
          <div
            className={cn(
              'flex cursor-pointer items-center px-3 opacity-30 transition-colors duration-200',
              props.currentPage + 1 <= totalPages && 'opacity-100 hover:bg-gainsborough dark:hover:bg-almost-light-gray',
            )}
            onClick={() => props.currentPage + 1 <= totalPages && onChangePage(props.currentPage + 1)}
          >
            <Icon
              className={cn('rotate-180 dark:text-white/80', props.currentPage + 1 <= totalPages && 'text-almost-light-gray')}
              icon={<ChevronIcon />}
            />
          </div>
          <div
            className={cn(
              'flex cursor-pointer items-center justify-center px-3 transition-colors duration-200',
              props.currentPage + 1 > totalPages && 'bg-light dark:bg-slate dark:opacity-60',
              PaletteUtils.isInsigniaPalette && [
                props.currentPage + 1 <= totalPages && ['bg-soft-black dark:bg-slate', 'hover:bg-soft-black/80 dark:hover:bg-slate/60'],
              ],
              PaletteUtils.isVcfcPalette && [
                'rounded-br-xl rounded-tr-xl',
                props.currentPage + 1 <= totalPages && ['bg-primary dark:bg-white/50', 'hover:bg-primary/80 dark:hover:bg-white/70'],
              ],
            )}
            onClick={() => props.currentPage + 1 <= totalPages && onChangePage(totalPages)}
          >
            <Icon icon={<FiChevronIcon />} className='rotate-180' color={PaletteUtils.getColor('primary', 'white')} />
          </div>
        </div>
        <Text className='my-auto text-nowrap' size='sm' color={ColorsEnum.Secondary}>
          {t('pagination.result', { start: firstItemNumber, end: lastItemNumber, total: props.totalItemCount })}
        </Text>
      </div>
      {!props.noSelect && (
        <div className={cn('flex', PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-3')}>
          <Text className='my-auto mr-4 text-nowrap' size='sm' color={ColorsEnum.Secondary}>
            {props.perPageLabel ?? t('pagination.perPage.default')}
          </Text>
          <Select
            items={Object.values(TablePaginationCountOfItems)
              .filter((x) => Number.isInteger(x))
              .map((x) => x.toString())}
            nullValue={{ isEnable: false }}
            value={props.countItemPage.toString()}
            onChange={(value) => {
              const result = parseInt(value!);
              props.onChangePage(1);
              props.onChangeCountItemPage(result);
            }}
          />
        </div>
      )}
    </div>
  );
};
